import type { Geometry, Polygon } from 'geojson';
import type { SurveyRow } from 'components/survey-description';
import type { VerifiedUpload } from 'types/area-geometry';
import type {
  AreaDetails,
  BoundaryStyle,
  CreationMethod,
  SelectedArea,
  SelectionKind,
} from './create-area-types';
import { circleFromPolygon } from './utils';

/** Everything the wire `source` is built from, whichever method is chosen. */
export interface AreaSourceInputs {
  method: CreationMethod;
  surveyRows: SurveyRow[];
  drawnPolygons: Polygon[];
  selectedAreas: SelectedArea[];
  boundaryStyle: BoundaryStyle;
  /** Free text from the buffer field, so it is parsed here rather than trusted. */
  bufferFt: string;
  /** What the selected rows are. The catalog only ever selects areas. */
  selectionKind?: SelectionKind;
  /** The verified files together, for the upload method. */
  upload?: { result: VerifiedUpload; fileSignedIds: string[] } | null;
}

/**
 * The id list the backend reads for each kind of selection: areas by their id,
 * wells by their Map Query row id.
 *
 * @param {SelectionKind} kind - What the rows are.
 * @param {SelectedArea[]} rows - The selected rows.
 * @return {Record<string, unknown>} The one id field to send.
 */
const selectionIds = (
  kind: SelectionKind,
  rows: SelectedArea[]
): Record<string, unknown> => {
  if (kind === 'wells') return { well_ids: rows.map((row) => Number(row.id)) };
  return { area_geometry_ids: rows.map((row) => Number(row.id)) };
};

/**
 * How many separate polygons a geometry is made of. A Unit is normally one, so
 * more than one is worth flagging.
 *
 * @param {Geometry | null} geometry - The shape being created.
 * @return {number} Its polygon count.
 */
export const polygonCount = (geometry: Geometry | null): number => {
  if (!geometry) return 0;
  return geometry.type === 'MultiPolygon' ? geometry.coordinates.length : 1;
};

/**
 * The geometry inputs for the chosen method, as one kind-tagged object — the same
 * shape `POST /area_geometry_previews` takes, so a preview and the saved shape
 * cannot drift.
 *
 * @param {AreaSourceInputs} inputs - The panel's geometry inputs.
 * @return {Record<string, unknown>} The `source` payload.
 */
export const buildAreaSource = ({
  method,
  surveyRows,
  drawnPolygons,
  selectedAreas,
  boundaryStyle,
  bufferFt,
  selectionKind = 'areas',
  upload,
}: AreaSourceInputs): Record<string, unknown> => {
  if (method === 'upload') {
    // The verified polygons are the shape; the stored files ride along so the
    // area keeps each as lineage.
    return {
      type: 'upload',
      geojson: upload?.result.geojson,
      file_signed_ids: upload?.fileSignedIds ?? [],
      feature_count: upload?.result.verified,
    };
  }

  if (method === 'survey') {
    return {
      type: 'survey',
      rows: surveyRows
        .filter((row) => row.location)
        .map((row) => ({
          reference_location_id: row.location?.id,
          quarter_calls: row.quarterCalls,
        })),
    };
  }

  if (method === 'drawn') {
    return {
      type: 'drawn',
      // The endpoint takes one geometry, so several polygons go up as a single
      // multi-part shape — what the combine methods produce too.
      geojson:
        drawnPolygons.length === 1
          ? drawnPolygons[0]
          : {
              type: 'MultiPolygon',
              coordinates: drawnPolygons.map((polygon) => polygon.coordinates),
            },
    };
  }

  return {
    type: 'selected',
    ...selectionIds(selectionKind, selectedAreas),
    boundary_style: boundaryStyle,
    // Only the bounding hull takes a buffer, and the field is free text.
    buffer_ft: boundaryStyle === 'bounding' ? Number(bufferFt) || 0 : undefined,
  };
};

/**
 * The per-type metadata, read by the category routing to decide whether a Unit
 * writes a development area alongside the area.
 *
 * @param {boolean} isUnit - Whether the chosen type is Units.
 * @param {AreaDetails} details - The panel's detail fields.
 * @return {Record<string, unknown> | undefined} The metadata, for Units only.
 */
export const buildAreaMetadata = (isUnit: boolean, details: AreaDetails) => {
  return isUnit ? { horizontal_unit: details.horizontalUnit } : undefined;
};

/**
 * The circles among the drawn shapes, so the saved area can give them back as
 * circles to edit. Each was sent as its polygon; this names its centre and radius.
 *
 * @param {Polygon[]} polygons - The drawn shapes.
 * @return {{ center: number[], radius_ft: number }[]} The circles, in drawn order.
 */
export const drawnCircles = (polygons: Polygon[]) =>
  polygons.flatMap((polygon) => {
    const circle = circleFromPolygon(polygon);
    return circle
      ? [{ center: circle.center, radius_ft: circle.radiusFt }]
      : [];
  });
